import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ArrowUpRight, MessageSquarePlus } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { SectionMark } from "@/components/PageHeader";

/* "COSA DICONO I RIDER" — carousel of public Trailforks comments.
   quotes: [{ text, lang: "it"|"en", kind: "quote"|"summary", source, tag?: "warn" }] */
export default function RiderQuotes({ quotes = [], trailName, trailforksUrl, n = "01" }) {
    const { lang } = useLang();
    const en = lang === "en";
    const ref = useRef(null);
    const [active, setActive] = useState(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const onScroll = () => {
            const w = el.firstElementChild?.offsetWidth || 1;
            setActive(Math.round(el.scrollLeft / (w + 16)));
        };
        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [quotes.length]);

    const go = (i) => {
        const el = ref.current;
        if (!el) return;
        const w = el.firstElementChild?.offsetWidth || 1;
        const idx = Math.max(0, Math.min(quotes.length - 1, i));
        el.scrollTo({ left: idx * (w + 16), behavior: "smooth" });
    };

    const title = en ? "WHAT RIDERS SAY" : "COSA DICONO I RIDER";
    const tellUrl = trailforksUrl ? `${trailforksUrl.replace(/\/?$/, "/")}comments/` : null;
    const TellButton = ({ primary }) => tellUrl && (
        <a href={tellUrl} target="_blank" rel="noreferrer" data-testid="rider-quotes-tell"
            className={`${primary ? "btn-primary" : "btn-ghost"} !py-2.5 !px-4 !text-[11px] inline-flex items-center gap-1.5`}>
            <MessageSquarePlus size={13} /> {en ? "Tell us about this trail" : "Racconta questo trail"} <ArrowUpRight size={11} />
        </a>
    );

    return (
        <div data-testid="rider-quotes" className="min-w-0 max-w-full">
            <SectionMark n={n} title={title} right={
                quotes.length > 1 && (
                    <span className="hidden sm:flex items-center gap-1">
                        <button type="button" onClick={() => go(active - 1)} disabled={active === 0} data-testid="rider-quotes-prev"
                            className="w-8 h-8 inline-flex items-center justify-center border border-white/15 text-abnormal-paper hover:border-abnormal-acid hover:text-abnormal-acid disabled:opacity-30 transition-colors">
                            <ChevronLeft size={14} />
                        </button>
                        <button type="button" onClick={() => go(active + 1)} disabled={active >= quotes.length - 1} data-testid="rider-quotes-next"
                            className="w-8 h-8 inline-flex items-center justify-center border border-white/15 text-abnormal-paper hover:border-abnormal-acid hover:text-abnormal-acid disabled:opacity-30 transition-colors">
                            <ChevronRight size={14} />
                        </button>
                    </span>
                )
            } />

            {quotes.length === 0 ? (
                <div data-testid="rider-quotes-empty" className="border border-dashed border-white/15 p-6 md:p-8">
                    <p className="font-display uppercase text-2xl md:text-3xl leading-tight text-abnormal-paperDim">
                        {en ? "No public rider reviews yet." : "Nessuna recensione pubblica dei rider, per ora."}
                    </p>
                    <p className="mt-3 text-sm text-abnormal-paperDim/80 leading-relaxed">
                        {en
                            ? `Ridden ${trailName}? Leave a comment on Trailforks — we only publish what riders actually write.`
                            : `Hai fatto ${trailName}? Lascia un commento su Trailforks: qui pubblichiamo solo quello che scrivono davvero i rider.`}
                    </p>
                    <div className="mt-4"><TellButton primary /></div>
                </div>
            ) : (
                <>
                    <div ref={ref} data-testid="rider-quotes-track"
                        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-5 px-5 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {quotes.map((q, i) => (
                            <figure key={i} data-testid={`rider-quote-${i}`}
                                className={`snap-start shrink-0 w-[86%] sm:w-[70%] md:w-[48%] relative border p-5 md:p-7 bg-abnormal-charcoal transition-colors duration-300 ${
                                    q.tag === "warn" ? "border-abnormal-warn/50" : i === active ? "border-abnormal-acid/70" : "border-white/10"}`}>
                                <span aria-hidden className={`absolute -top-3 left-4 font-display text-6xl leading-none ${q.tag === "warn" ? "text-abnormal-warn" : "text-abnormal-acid"}`}>“</span>
                                <blockquote className="mt-4 text-abnormal-paper text-lg md:text-2xl leading-snug font-medium tracking-tight">
                                    {q.text}
                                </blockquote>
                                <figcaption className="mt-5 flex flex-wrap items-center gap-2 label-mono">
                                    <span className="border border-white/15 px-1.5 py-0.5 text-abnormal-paperDim">{(q.lang || "it").toUpperCase()}</span>
                                    {q.kind === "summary" && (
                                        <span className="border border-white/15 px-1.5 py-0.5 text-abnormal-paperDim/80">{en ? "SUMMARY" : "SINTESI"}</span>
                                    )}
                                    {q.tag === "warn" && (
                                        <span className="border border-abnormal-warn/60 text-abnormal-warn px-1.5 py-0.5">{en ? "HEADS UP" : "SEGNALAZIONE"}</span>
                                    )}
                                    {q.source && (
                                        <a href={q.source} target="_blank" rel="noreferrer" data-testid={`rider-quote-source-${i}`}
                                            className="ml-auto inline-flex items-center gap-1 text-abnormal-paperDim hover:text-abnormal-acid transition-colors">
                                            Trailforks <ArrowUpRight size={11} />
                                        </a>
                                    )}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                    {quotes.length > 1 && (
                        <div className="mt-3 flex items-center gap-1.5" data-testid="rider-quotes-dots">
                            {quotes.map((_, i) => (
                                <button key={i} type="button" onClick={() => go(i)} aria-label={`${i + 1}`}
                                    className={`h-1 transition-all duration-300 ${i === active ? "w-8 bg-abnormal-acid" : "w-3 bg-white/20 hover:bg-white/40"}`} />
                            ))}
                        </div>
                    )}
                    <div className="mt-4 flex flex-wrap items-start justify-between gap-3">
                        <p className="label-mono text-abnormal-paperDim/50 leading-relaxed max-w-md">
                            {en
                                ? "// Public rider comments and trail reports on Trailforks. Names, dates and ratings omitted. Not written by us."
                                : "// Commenti pubblici e trail report dei rider su Trailforks. Nomi, date e stelline omessi. Non scritti da noi."}
                        </p>
                        <TellButton />
                    </div>
                </>
            )}
        </div>
    );
}
